import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';
import {
  clearJourneySessionState,
  setStoredApplicationNumber,
  ensureStoredSessionRef,
} from './common/utils';
import { resolveJourneyApplicationNumbers } from './common/utils/journeyEncryption';

class ReactRootElement extends HTMLElement {
  constructor() {
    super();
    this._root = null;
  }

  disconnectedCallback() {
    if (!this._root) return;
    this._root.unmount();
    this._root = null;
  }
}

/**
 * Build initial in-app path and whether we cleared (so App can reset store).
 * - On payment-success / payment-failure: legacy fallback when this CX is still
 *   mounted on shared payment pages. Prefer `inlic-common-payment-return` on those
 *   Liferay pages (do not mount journey CX + common CX together).
 * - Direct gateway callback onto term-plan/term-insurance with status: keep PaymentReturn here.
 * - On term-plan with fromPaymentReturn + applicationNumber: hydrate/resume in this journey.
 * @returns {{ path: string | null, resetJourneyOnMount: boolean }}
 */
function getInitialPath() {
  if (typeof window === 'undefined' || !window.location) return { path: null, resetJourneyOnMount: true };
  const pathname = window.location.pathname || '';
  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  const replaceBrowserUrlIfPossible = () => {
    if (typeof window === 'undefined') return;
    if (!window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      const raw =
        (url.searchParams.get('applicationNumber') || url.searchParams.get('applicationNo') || '')
          .trim();
      if (!raw) return;
      const { plain, encrypted } = resolveJourneyApplicationNumbers(raw);
      if (!plain || !encrypted) return;
      url.searchParams.set('applicationNumber', encrypted);
      url.searchParams.delete('applicationNo');
      window.history.replaceState(null, document.title || '', `${url.pathname}?${url.searchParams.toString()}`);
    } catch {
      // ignore
    }
  };
  if (pathname.includes('track-your-application')) {
    return { path: '/track-your-application', resetJourneyOnMount: false };
  }
  if (pathname.includes('payment-success')) {
    replaceBrowserUrlIfPossible();
    if (!params.has('status')) params.set('status', 'SUCCESS');
    return { path: `/payment-return?${params.toString()}`, resetJourneyOnMount: false };
  }
  if (pathname.includes('payment-failure')) {
    replaceBrowserUrlIfPossible();
    if (!params.has('status')) params.set('status', 'FAILED');
    return { path: `/payment-return?${params.toString()}`, resetJourneyOnMount: false };
  }
  // Direct gateway callback: term-insurance/term-plan?applicationNumber=X&status=SUCCESS|FAILED&...
  // Route through payment-return so we can resume from the correct stage instead of resetting to lead.
  if ((pathname.includes('term-insurance') || pathname.includes('term-plan')) &&
      params.get('status') && params.get('applicationNumber')) {
    return { path: `/payment-return?${params.toString()}`, resetJourneyOnMount: false };
  }
  // Return from payment: always enter through /term-plan so the app can hydrate from journey
  // and decide the destination from a single backend source of truth.
  const applicationNumber = params.get('applicationNumber');
  const fromPaymentReturn = params.get('fromPaymentReturn');
  if (fromPaymentReturn === '1' && applicationNumber &&
      (pathname.includes('term-insurance') || pathname.includes('term-plan') || pathname === '/' || pathname === '')) {
    return {
      path: `/term-plan?applicationNumber=${encodeURIComponent(applicationNumber)}&fromPaymentReturn=1`,
      resetJourneyOnMount: false,
    };
  }
  // term-plan page: if we have applicationNumber in URL (e.g. from lead basic redirect), start journey there
  if (pathname.includes('term-plan')) {
    const appNumFromUrl = params.get('applicationNumber')?.trim();
    if (appNumFromUrl) {
      const { plain } = resolveJourneyApplicationNumbers(appNumFromUrl);
      if (plain) setStoredApplicationNumber(plain);
      return { path: `/term-plan${search || ''}`, resetJourneyOnMount: false };
    }
    // Refresh on term-plan without app number = clear session and go to start path (term-plan)
    clearJourneySessionState({ clearLocalStorage: true });
    return { path: '/term-plan', resetJourneyOnMount: true };
  }
  // CX on home or other page: clear session so refresh on any stage shows start path, not last screen
  clearJourneySessionState({ clearLocalStorage: true });
  return { path: null, resetJourneyOnMount: true };
}

class TermInsuranceJourneyElement extends ReactRootElement {
  connectedCallback() {
    if (!this._root) {
      ensureStoredSessionRef();
      const { path: paymentPath, resetJourneyOnMount } = getInitialPath();
      const initialStage = this.getAttribute('stage') || 'lead';
      this._root = createRoot(this);
      const app = paymentPath != null
        ? <App initialPath={paymentPath} resetJourneyOnMount={resetJourneyOnMount} />
        : <App initialStage={initialStage} resetJourneyOnMount={resetJourneyOnMount} />;
      this._root.render(
        import.meta.env.DEV ? <React.StrictMode>{app}</React.StrictMode> : app
      );
    }
  }
}

const ELEMENT_NAME = 'term-insurance-journey';
if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, TermInsuranceJourneyElement);
}
